/**
 * Mock data for the Posting Log (Reports → Posting Log) demo mode.
 *
 * Mirrors the shape returned by GET /api/costing-transactions so the Posting Log
 * page, its drill-down panel, retry and export flows all work the same way in
 * demo mode as they do against the real backend.
 *
 * Covers every posting outcome you can see in the UI:
 *  - a fully posted timesheet (all lines succeeded)
 *  - a fully failed timesheet (all lines failed)
 *  - partially failed timesheets (a mix of posted and failed lines)
 */
import type {
  CostingIntegrationStatus,
  CostingTransactionLogDto,
  PagedCostingTransactionLogResponse,
  PostingLogQueryParams,
  ResendCostingTransactionResult,
} from '@/services/postingLogApi';

const DAY_MS = 24 * 60 * 60 * 1000;

/** Sunday of the week `weeksAgo` weeks before today, used as a stable transaction date. */
function weekEndingDate(weeksAgo: number): Date {
  const now = new Date();
  const day = now.getDay(); // 0 = Sunday
  const daysSinceSunday = day === 0 ? 0 : day;
  const thisWeekEnding = new Date(now.getTime() - daysSinceSunday * DAY_MS);
  return new Date(thisWeekEnding.getTime() - weeksAgo * 7 * DAY_MS);
}

function iso(date: Date): string {
  return date.toISOString().slice(0, 10);
}

interface MockLineSpec {
  dayOffset: number; // offset from weekEnding (0 = weekEnding day)
  projectCode: string;
  projectName: string;
  costCentreCode: string;
  costCentreName: string;
  quantity: number;
  price: number;
  success: boolean;
  errorMessage?: string;
}

interface MockTimesheetSpec {
  timesheetId: number;
  employeeId: number;
  employeeName: string;
  resourceCode: string;
  weeksAgo: number;
  lines: MockLineSpec[];
}

const TIMESHEET_SPECS: MockTimesheetSpec[] = [
  // Fully posted — James Smith, last week
  {
    timesheetId: 201,
    employeeId: 1,
    employeeName: 'James Smith',
    resourceCode: 'RC-001',
    weeksAgo: 1,
    lines: [
      { dayOffset: -4, projectCode: 'project-1', projectName: 'Core Platform Upgrade', costCentreCode: 'cc-dev', costCentreName: 'Development', quantity: 7.5, price: 45, success: true },
      { dayOffset: -3, projectCode: 'project-1', projectName: 'Core Platform Upgrade', costCentreCode: 'cc-dev', costCentreName: 'Development', quantity: 7.5, price: 45, success: true },
      { dayOffset: -2, projectCode: 'project-1', projectName: 'Core Platform Upgrade', costCentreCode: 'cc-dev', costCentreName: 'Development', quantity: 7.5, price: 45, success: true },
    ],
  },
  // Fully failed — Sarah Jones, last week
  {
    timesheetId: 202,
    employeeId: 2,
    employeeName: 'Sarah Jones',
    resourceCode: 'RC-002',
    weeksAgo: 1,
    lines: [
      { dayOffset: -4, projectCode: 'project-2', projectName: 'Client Migration', costCentreCode: 'cc-test', costCentreName: 'Testing', quantity: 7.5, price: 40, success: false, errorMessage: 'Cost centre CC-TEST is not open for posting period 2026-01' },
      { dayOffset: -3, projectCode: 'project-2', projectName: 'Client Migration', costCentreCode: 'cc-test', costCentreName: 'Testing', quantity: 7.5, price: 40, success: false, errorMessage: 'Cost centre CC-TEST is not open for posting period 2026-01' },
    ],
  },
  // Partially failed — William Johnson, 2 weeks ago
  {
    timesheetId: 203,
    employeeId: 11,
    employeeName: 'William Johnson',
    resourceCode: 'RC-011',
    weeksAgo: 2,
    lines: [
      { dayOffset: -4, projectCode: 'project-1', projectName: 'Core Platform Upgrade', costCentreCode: 'cc-dev', costCentreName: 'Development', quantity: 7.5, price: 50, success: true },
      { dayOffset: -3, projectCode: 'project-1', projectName: 'Core Platform Upgrade', costCentreCode: 'cc-dev', costCentreName: 'Development', quantity: 7.5, price: 50, success: true },
      { dayOffset: -2, projectCode: 'project-1', projectName: 'Core Platform Upgrade', costCentreCode: 'cc-dev', costCentreName: 'Development', quantity: 7.5, price: 50, success: false, errorMessage: 'Resource code RC-011 not found in Access Dimensions' },
      { dayOffset: -1, projectCode: 'project-1', projectName: 'Core Platform Upgrade', costCentreCode: 'cc-dev', costCentreName: 'Development', quantity: 6.0, price: 50, success: false, errorMessage: 'Resource code RC-011 not found in Access Dimensions' },
    ],
  },
  // Fully posted — Hannah Walker, 3 weeks ago
  {
    timesheetId: 204,
    employeeId: 12,
    employeeName: 'Hannah Walker',
    resourceCode: 'RC-012',
    weeksAgo: 3,
    lines: [
      { dayOffset: -4, projectCode: 'project-3', projectName: 'Internal PMO', costCentreCode: 'cc-pm', costCentreName: 'Project Management', quantity: 7.5, price: 55, success: true },
      { dayOffset: -3, projectCode: 'project-3', projectName: 'Internal PMO', costCentreCode: 'cc-pm', costCentreName: 'Project Management', quantity: 7.5, price: 55, success: true },
      { dayOffset: -2, projectCode: 'project-3', projectName: 'Internal PMO', costCentreCode: 'cc-pm', costCentreName: 'Project Management', quantity: 7.5, price: 55, success: true },
    ],
  },
  // Fully failed — Thomas Wright, current week
  {
    timesheetId: 205,
    employeeId: 13,
    employeeName: 'Thomas Wright',
    resourceCode: 'RC-013',
    weeksAgo: 0,
    lines: [
      { dayOffset: -4, projectCode: 'project-4', projectName: 'Architecture Review', costCentreCode: 'cc-arch', costCentreName: 'Architecture', quantity: 7.5, price: 60, success: false, errorMessage: 'Project code PRJ-004 is closed for time posting' },
      { dayOffset: -3, projectCode: 'project-4', projectName: 'Architecture Review', costCentreCode: 'cc-arch', costCentreName: 'Architecture', quantity: 7.5, price: 60, success: false, errorMessage: 'Project code PRJ-004 is closed for time posting' },
    ],
  },
  // Partially failed — James Smith, 4 weeks ago
  {
    timesheetId: 206,
    employeeId: 1,
    employeeName: 'James Smith',
    resourceCode: 'RC-001',
    weeksAgo: 4,
    lines: [
      { dayOffset: -4, projectCode: 'project-1', projectName: 'Core Platform Upgrade', costCentreCode: 'cc-dev', costCentreName: 'Development', quantity: 7.5, price: 45, success: true },
      { dayOffset: -3, projectCode: 'project-1', projectName: 'Core Platform Upgrade', costCentreCode: 'cc-dev', costCentreName: 'Development', quantity: 7.5, price: 45, success: true },
      { dayOffset: -2, projectCode: 'project-1', projectName: 'Core Platform Upgrade', costCentreCode: 'cc-dev', costCentreName: 'Development', quantity: 7.5, price: 45, success: true },
      { dayOffset: -1, projectCode: 'project-2', projectName: 'Client Migration', costCentreCode: 'cc-test', costCentreName: 'Testing', quantity: 12.0, price: 45, success: false, errorMessage: 'Quantity exceeds maximum daily hours for resource RC-001' },
      { dayOffset: 0, projectCode: 'project-2', projectName: 'Client Migration', costCentreCode: 'cc-test', costCentreName: 'Testing', quantity: 7.5, price: 45, success: false, errorMessage: 'Quantity exceeds maximum daily hours for resource RC-001' },
    ],
  },
];

function buildMockLines(): CostingTransactionLogDto[] {
  const lines: CostingTransactionLogDto[] = [];
  let logId = 1;
  let timesheetLineId = 1;

  for (const ts of TIMESHEET_SPECS) {
    const weekEnding = weekEndingDate(ts.weeksAgo);
    for (const line of ts.lines) {
      const transactionDate = new Date(weekEnding.getTime() + line.dayOffset * DAY_MS);
      lines.push({
        id: logId++,
        timesheetId: ts.timesheetId,
        weekEnding: iso(weekEnding),
        timesheetLineId: timesheetLineId++,
        employeeId: ts.employeeId,
        employeeName: ts.employeeName,
        resourceCode: ts.resourceCode,
        periodYear: String(transactionDate.getFullYear()),
        periodNumber: Math.ceil((transactionDate.getDate()) / 7),
        transactionDate: iso(transactionDate),
        reference: `${ts.timesheetId}-${timesheetLineId}`,
        projectCode: line.projectCode,
        projectName: line.projectName,
        costCentreCode: line.costCentreCode,
        costCentreName: line.costCentreName,
        quantity: line.quantity,
        price: line.price,
        upliftValue: 0,
        success: line.success,
        externalTransactionId: line.success ? `TXN-${logId}` : null,
        errorMessage: line.success ? null : line.errorMessage,
      });
    }
  }
  return lines;
}

/** Mutable in-memory store so demo retries can "fix" a failed line, just like a real re-send would. */
let mockLines: CostingTransactionLogDto[] = buildMockLines();

const MOCK_INTEGRATION: CostingIntegrationStatus = {
  isConfigured: true,
  productTypeCode: 'dimensions',
  productTypeName: 'Access Dimensions',
  instanceName: 'Demo Instance',
};

function matchesSearch(line: CostingTransactionLogDto, search: string): boolean {
  const needle = search.toLowerCase();
  return [line.employeeName, line.projectName, line.projectCode, line.costCentreName, line.costCentreCode, line.reference]
    .some((value) => value?.toLowerCase().includes(needle));
}

/**
 * A timesheet only counts as "Posted" when every one of its lines posted — a single failed
 * line makes the whole timesheet "Failed", mirroring the header status the UI derives via
 * groupPostingLogRows. Used so the Posted/Failed status filter is applied per-timesheet
 * rather than per-line: filtering to "Posted" must never surface a timesheet that still has
 * failed lines (just with those lines hidden), and filtering to "Failed" must show every line
 * of a partially-failed timesheet, including the ones that did post.
 */
function timesheetFullyPosted(timesheetId: number, lines: CostingTransactionLogDto[] = mockLines): boolean {
  return lines.every((l) => l.timesheetId !== timesheetId || l.success);
}

function filterMockLines(params: PostingLogQueryParams): CostingTransactionLogDto[] {
  let items = mockLines;

  if (params.status) {
    const wantPosted = params.status === 'posted';
    items = items.filter((l) => timesheetFullyPosted(l.timesheetId) === wantPosted);
  }
  if (params.employeeIds?.length) items = items.filter((l) => params.employeeIds!.includes(l.employeeId));
  if (params.projectCodes?.length) items = items.filter((l) => l.projectCode && params.projectCodes!.includes(l.projectCode));
  if (params.costCentreCodes?.length) items = items.filter((l) => l.costCentreCode && params.costCentreCodes!.includes(l.costCentreCode));
  if (params.timesheetId) items = items.filter((l) => l.timesheetId === params.timesheetId);
  if (params.dateFrom) items = items.filter((l) => l.transactionDate && new Date(l.transactionDate) >= params.dateFrom!);
  if (params.dateTo) items = items.filter((l) => l.transactionDate && new Date(l.transactionDate) <= params.dateTo!);
  if (params.search?.trim()) items = items.filter((l) => matchesSearch(l, params.search!.trim()));

  return items;
}

/** Returns every mock line matching the filters (no pagination), shaped like the live API response. */
export function getMockPostingLogData(params: PostingLogQueryParams = {}): PagedCostingTransactionLogResponse {
  const items = filterMockLines(params);
  const timesheetIds = [...new Set(items.map((l) => l.timesheetId))];
  const postedCount = timesheetIds.filter((id) => timesheetFullyPosted(id, items)).length;
  return {
    items,
    totalCount: items.length,
    postedCount,
    failedCount: timesheetIds.length - postedCount,
    timesheetCount: timesheetIds.length,
    pageNumber: 1,
    pageSize: Math.max(items.length, 1),
    integration: MOCK_INTEGRATION,
  };
}

/** Simulates GET /api/costing-transactions/{logId}/resend for demo mode. */
export function resendMockPostingLogLine(logId: number): ResendCostingTransactionResult {
  const line = mockLines.find((l) => l.id === logId);
  if (!line) {
    return { status: 'NotFound', errors: ['This posting line could not be found.'], warnings: [], success: false };
  }
  line.success = true;
  line.errorMessage = null;
  line.externalTransactionId = `TXN-RESENT-${logId}`;
  mockLines = [...mockLines];
  return { status: 'Success', transactionId: line.externalTransactionId, errors: [], warnings: [], success: true };
}

/** Test-only / dev-tool escape hatch to reset the demo dataset back to its initial state. */
export function resetMockPostingLogData(): void {
  mockLines = buildMockLines();
}
